// src/paginas/Reportes/EntradaReportes.jsx
import { useEffect, useState } from "react";
import "../../paginas/Reportes/reportes.css";
import { ProgressSpinner } from "primereact/progressspinner";
import { Chart } from "primereact/chart";
import { enumReporteSeleccionado } from "../FuncionesLocales.js";
import { useStoreHome } from "../../paginas/Home/useStoreHome.js";
import { useStoreReportes } from "../../paginas/Reportes/useStoreReportes.js";
import Calendario from "../../ComponentesGlobales/Calendario.jsx";

const pickValue = (_unidad, row) => Number(row.Peso ?? row.nPeso ?? 0);

const PROCESS_COLORS = [
  "#1F3E2E", "#29513C", "#33654B", "#3D795A", "#478D68",
  "#51A077", "#60AF86", "#74B995", "#88C3A4", "#9CCDB3",
];


const EntradaReportes = () => {
  const FechaDesde = useStoreReportes((state) => state.FechaDesde);
  const FechaHasta = useStoreReportes((state) => state.FechaHasta);
  const BuscandoListaEntradas = useStoreReportes((state) => state.BuscandoEntradasSalidas);
  const ListaDetalle = useStoreReportes((state) => state.ListaEntradas);
  const TipoProducto = useStoreReportes((state) => state.TipoProducto);
  const [chartsByUnitEntradas, setChartsByUnitEntradas] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    useStoreHome.setState({ ReportesSeleccionado: enumReporteSeleccionado.PRODUCCION });
  }, []);

  useEffect(() => {
    useStoreReportes.getState().getEntradasSalidas();
  }, [FechaDesde, FechaHasta, TipoProducto]);


  // ENTRADAS
  useEffect(() => {
    const textColor = "black";
    const textColorSecondary = "black";
    const surfaceBorder = getComputedStyle(document.documentElement)
      .getPropertyValue("--surface-border");

    if (ListaDetalle && ListaDetalle.length > 0) {
      // 1) agrupar por unidad
      const groupedByUnit = ListaDetalle.reduce((acc, item) => {
        const unidad = item.UnidadDescripcion || "Sin unidad";
        (acc[unidad] ??= []).push(item);
        return acc;
      }, {});

      const PALETTE = [
        "#1F3E2E", "#29513C", "#33654B", "#3D795A", "#478D68",
        "#51A077", "#60AF86", "#74B995", "#88C3A4", "#9CCDB3",
      ];

      const chartsByUnitData = {};

      Object.keys(groupedByUnit).forEach((unidad) => {
        const unitData = groupedByUnit[unidad];

        // 2) agregar por MercaderiaDescripcion (normalizando)
        const agregados = unitData.reduce((acc, it) => {
          const raw = it.MercaderiaDescripcion || "Sin descripción";
          const key = raw.trim().toLowerCase();
          if (!acc[key]) acc[key] = { label: raw, total: 0, unidades: 0, peso: 0 };

          // usa el helper para sumar según unidad (unidades o peso)
          const val = pickValue(unidad, it);
          acc[key].total += Number(val || 0);
          acc[key].unidades += Number(it.Unidades ?? it.nUnidades ?? 0);
          acc[key].peso += Number(it.Peso ?? it.nPeso ?? 0);
          return acc;
        }, {});

        // 3) ordenar desc por total
        const ordenados = Object.values(agregados).sort((a, b) => b.total - a.total);

        // 4) armar dataset ya agregado
        const labels = ordenados.map(o => o.label);
        const data = ordenados.map(o => o.total);

        chartsByUnitData[unidad] = {
          labels,
          datasets: [
            {
              label: unidad, // p.ej. "Unidades" o "Kg"
              backgroundColor: PALETTE.slice(0, labels.length),
              borderColor: PALETTE.slice(0, labels.length),
              data,
            },
          ],
        };
      });

      setChartsByUnitEntradas(chartsByUnitData);
    } else {
      setChartsByUnitEntradas({});
    }

    setChartOptions({
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: { labels: { color: textColor } },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = Number(ctx.parsed.y) || 0;
              return ` ${v.toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: { color: textColorSecondary, font: { weight: 500 }, maxRotation: 90, minRotation: 0 },
          grid: { display: true, drawBorder: true },
        },
        y: {
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder, drawBorder: false },
        },
      },
    });
  }, [ListaDetalle]);


  // estado para las tortas por proceso
  const [procesoPieChartsByUnit, setProcesoPieChartsByUnit] = useState({});

  // ---- Torta por PROCESO ----
  useEffect(() => {

    const fuente = ListaDetalle?.length ? ListaDetalle : [];

    if (!fuente.length) {
      setProcesoPieChartsByUnit({});
      return;
    }

    // agrupar por UnidadDescripcion
    const byUnit = fuente.reduce((acc, it) => {
      const unidad = it.UnidadDescripcion || 'Sin unidad';
      (acc[unidad] ||= []).push(it);
      return acc;
    }, {});

    const result = {};

    Object.keys(byUnit).forEach((unidad) => {
      const arr = byUnit[unidad];

      //agrupar por Proceso
      const byProcess = arr.reduce((acc, it) => {
        const p = it.Proceso || 'Sin proceso';
        (acc[p] ||= 0);
        acc[p] += pickValue(unidad, it);
        return acc;
      }, {});

      // ordenar desc 
      const procesos = Object.entries(byProcess)
        .sort((a, b) => b[1] - a[1])
        .map(([proc, total]) => ({ proc, total }));

      // armar dataset
      result[unidad] = {
        data: {
          labels: procesos.map(x => x.proc),
          datasets: [{
            data: procesos.map(x => x.total),
            backgroundColor: PROCESS_COLORS,
            borderColor: "#FFFFFF",
            borderWidth: 2
          }]
        },
        options: {
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: 'black', usePointStyle: true, padding: 15, font: { size: 11 } }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const v = Number(ctx.parsed) || 0;
                  const total = ctx.dataset.data.reduce((a, b) => a + Number(b || 0), 0);
                  const pct = total ? ((v / total) * 100).toFixed(1) : 0;
                  return `${ctx.label}:(${pct}%)`;
                }
              }
            }
          },
          maintainAspectRatio: false
        }
      };
    });

    setProcesoPieChartsByUnit(result);
  }, [ListaDetalle]);

//console.log (ListaDetalle)
  return (
    <div className="reportes-container">
      <h3 className="subtitulo-reportes" style={{ color: '#1f6842ff' }}>Entradas</h3>

      <div className="filtros-fecha" style={{ marginLeft: 10 }} >
        <div>
          <span style={{ marginTop: 10, marginRight: 10 }}>Desde: </span>
          <Calendario
            Fecha={FechaDesde}
            setFecha={(fecha) => useStoreReportes.setState({ FechaDesde: fecha })}
          />
        </div>
        <div>
          <span style={{ marginTop: 10, marginRight: 10 }}>Hasta:</span>
          <Calendario
            Fecha={FechaHasta}
            setFecha={(fecha) => useStoreReportes.setState({ FechaHasta: fecha })}
          />
        </div>
      </div>


      {BuscandoListaEntradas ? (
        <div className="centrado-400">
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="8" />
        </div>
      ) : (
        <div className="graficos-reportes">
          {Object.keys(chartsByUnitEntradas).length > 0 ? (
            Object.keys(chartsByUnitEntradas).map((unidad) => {
              const unitData = ListaDetalle.filter(
                (item) => (item.UnidadDescripcion || "Sin unidad") === unidad
              );

              const totalPeso = parseFloat(unitData.reduce((acc, curr) => acc + Number(curr.Peso ?? curr.nPeso ?? 0), 0));
              return (
                <div key={`entradas-${unidad}`} className="bloque-unidad" style={{display:'flex',justifyContent:'center'}}>

                  <div className="fila-reportes">
                    {/* <h4 className="titulo-bloque">Entradas por {unidad}</h4> */}
                    {/* Barras */}
                    <div className="charts-row"
                      style={{
                        display: 'flex',
                        gap: '5px',
                        alignItems: 'start',
                        justifyContent: 'center',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        paddingBottom: '10px',
                        width: '100%',
                        height: '400px'
                      }}>
                      <Chart
                        type="bar"
                        data={chartsByUnitEntradas[unidad]}
                        options={chartOptions}
                        style={{ height: "400px", padding: "5px" }}
                      />
                    </div>

                    {/* Totales */}
                    <div className="circulos-wrapper" style={{ marginTop: 50 }}>
                      <div className="circle-container">
                        <div className="circle-content">
                          <div className="circle-number">
                            {parseFloat(totalPeso.toFixed(2))}
                          </div>
                          <div className="circle-label">{unidad}</div>
                        </div>
                      </div>

                    </div>

                    {/* Gráfico de torta por PROCESO para esta unidad */}
                    {procesoPieChartsByUnit[unidad] && (
                      <div className="card" style={{ minHeight: '350px', padding: '10px', width: '100%' }}>
                        <h4 className="titulo-bloque">Distribución por Proceso - {unidad}</h4>
                        <Chart
                          type="pie"
                          data={procesoPieChartsByUnit[unidad].data}
                          options={procesoPieChartsByUnit[unidad].options}
                          style={{ height: "300px", width: "100%", padding: "5px" }}
                        />
                      </div>
                    )}


                  </div>
                </div>
              );
            })
          ) : (
            <div className="sin-datos">📈 Sin datos de entradas</div>
          )}
        </div>
      )}
    </div>
  );
};

export default EntradaReportes;
